import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, DatePicker, Popconfirm, message, Space, Tag } from "antd";
import { Plus } from "lucide-react";
import dayjs from "dayjs";
import { useDBSList, useDBSCreate, useDBSUpdate, useDBSDelete, useCCUsers } from "../../../../api-hooks/compliance";

const STATUS_COLOR: Record<string, string> = { clear: "green", has_information: "orange", pending: "blue" };

export default function DBSTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [modal, setModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });
  const [form] = Form.useForm();
  const [page, setPage] = useState(1);

  const { data: res, isLoading } = useDBSList(cid, { page, limit: 20 });
  const { data: users = [] } = useCCUsers(cid);
  const rows = (res as any)?.data ?? [];
  const total = (res as any)?.total ?? 0;

  const cb = (msg: string) => ({ onSuccess: () => { msgApi.success(msg); setModal({ open: false, rec: null }); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const create = useDBSCreate(cid, cb("DBS check added."));
  const update = useDBSUpdate(cid, cb("Updated."));
  const remove = useDBSDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  function openEdit(r?: any) {
    setModal({ open: true, rec: r ?? null });
    form.setFieldsValue(r ? { ...r, issue_date: r.issue_date ? dayjs(r.issue_date) : null, expiry_date: r.expiry_date ? dayjs(r.expiry_date) : null } : { check_level: "basic", status: "pending" });
  }

  const columns = [
    { title: "Worker", dataIndex: "user_name", key: "u" },
    { title: "Level", dataIndex: "check_level", key: "l", render: (v: string) => v?.replace(/_/g, " ").toUpperCase() },
    { title: "Cert #", dataIndex: "certificate_number", key: "cn", render: (v: string) => v || "—" },
    { title: "Issued", dataIndex: "issue_date", key: "i", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Expiry", dataIndex: "expiry_date", key: "e", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Status", dataIndex: "status", key: "s", render: (v: string) => <Tag color={STATUS_COLOR[v]}>{v?.replace(/_/g, " ").toUpperCase()}</Tag> },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => remove.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  return (
    <div>
      {ctx}
      <div className="flex justify-between mb-3">
        <h3 className="font-semibold text-slate-700 text-sm">DBS Checks</h3>
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => openEdit()} style={{ background: "#4c1d95" }}>Add DBS</Button>
      </div>
      <Table rowKey="id" dataSource={rows} columns={columns} loading={isLoading} size="small" pagination={{ current: page, pageSize: 20, total, onChange: setPage }} />
      <Modal open={modal.open} onCancel={() => setModal({ open: false, rec: null })} footer={null} destroyOnClose title={modal.rec ? "Edit DBS Check" : "New DBS Check"} width={480}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          const p = { ...vals, issue_date: vals.issue_date?.format("YYYY-MM-DD"), expiry_date: vals.expiry_date?.format("YYYY-MM-DD") };
          if (modal.rec) update.mutate({ id: modal.rec.id, ...p }); else create.mutate(p);
        }}>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="user_id" label="Worker" rules={[{ required: true }]}><Select showSearch options={(users as any[]).map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}`.trim() || u.email }))} /></Form.Item>
            <Form.Item name="check_level" label="Check Level" rules={[{ required: true }]}><Select options={["basic","standard","enhanced","enhanced_barred"].map(v => ({ value: v, label: v.replace(/_/g," ").toUpperCase() }))} /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="certificate_number" label="Certificate #"><Input /></Form.Item>
            <Form.Item name="checked_by" label="Checked By (User ID)"><Input type="number" /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="issue_date" label="Issue Date" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="expiry_date" label="Expiry Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="status" label="Status" rules={[{ required: true }]}><Select options={[{ value: "pending", label: "Pending" }, { value: "clear", label: "Clear" }, { value: "has_information", label: "Has Information" }]} /></Form.Item>
            <Form.Item name="document_url" label="Document URL"><Input /></Form.Item>
          </div>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={create.isPending || update.isPending}>Save</Button>
            <Button onClick={() => setModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
